import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BedIcon } from "../../assets/bed.svg";
import { ReactComponent as BathIcon } from "../../assets/bath.svg";
import { ReactComponent as AreaIcon } from "../../assets/area.svg";
import { buildImageUrl } from "../../utils/imageUrl";
import { useLanguage } from '../../contexts/LanguageContext';
import { PropertyStatus } from '../../core/api/request/home/hot-property/types';


export type Property = {
  id: number;
  property_name: string;
  price: string;
  size: string;
  bedrooms: number;
  bathrooms: number;
  description: string;
  thumbnail_image: string;
  property_status: PropertyStatus;
  property_status_display: string;
  property_type?: {
    name: string;
    name_kh?: string;
    name_zh?: string;
  };
  district?: {
    name: string;
    name_kh?: string;
    name_zh?: string;
  };
  commune?: {
    name: string;
    name_kh?: string;
    name_zh?: string;
  };
  is_hot?: boolean;
};

type Props = {
  property: Property;
  type: string; // Add type prop
  returnUrl?: string; // URL search params to preserve when navigating back
};

const OVERLAY_BG = 'linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.60) 100%)';
const OVERLAY_TEXT_COLOR = '#fff';
const STATUS_BG = '#e9f0fa';
const STATUS_COLOR = '#1a237e';
const PRICE_COLOR = '#fff';
const PRICE_SHADOW = '0 2px 8px rgba(0,0,0,0.18)';

export const PropertyCard: React.FC<Props> = ({ property, type, returnUrl }) => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/property/${type}/${property.id}`, {
      state: { returnUrl }
    });
  };

  const getTranslatedName = (item: { name: string; name_kh?: string; name_zh?: string }) => {
    const lang = currentLanguage?.toLowerCase();
    if (lang?.startsWith('km') || lang?.startsWith('kh')) return item.name_kh || item.name;
    if (lang?.startsWith('zh')) return item.name_zh || item.name;
    return item.name;
  };

  const getStatusLabel = (status: string) => {
    if (status === 'rent') return t('property.status.rent');
    if (status === 'sale') return t('property.status.sale');
    return property.property_status_display;
  };

  const formatTitle = () => {
    const parts = [];

    if (property.bedrooms) {
      const isEnglish = currentLanguage?.startsWith('en');
      parts.push(`${property.bedrooms} ${property.bedrooms > 1 && isEnglish ? t('property.detail.bedroom') + 's' : t('property.detail.bedroom')},`);
    }

    if (property.property_type) {
      parts.push(getTranslatedName(property.property_type));
    }

    if (property.property_status) {
      parts.push(`${t('common.for')} ${getStatusLabel(property.property_status)}`);
    }

    if (property.district) {
      parts.push(`, ${getTranslatedName(property.district)}`);
    }

    if (property.commune) {
      parts.push(`, ${getTranslatedName(property.commune)}`);
    }


    return parts.join(' ');
  };

  return (
    <CardContainer onClick={handleClick}>
      <ImageContainer>
        <StyledImage
          src={buildImageUrl(property.thumbnail_image)}
          alt={property.property_name}
        />
        <StatusTag>{getStatusLabel(property.property_status)}</StatusTag>
        <CardContentOverlay>
          <Title>{formatTitle()}</Title>
          <InfoRow>
            <InfoItem>
              <span role="img" aria-label="bed"><BedIcon /></span> {property.bedrooms} {property.bedrooms > 1 && currentLanguage?.startsWith('en') ? t('property.detail.bedroom') + 's' : t('property.detail.bedroom')}
            </InfoItem>
            <InfoItem>
              <span role="img" aria-label="bath"><BathIcon /></span> {property.bathrooms} {property.bathrooms > 1 && currentLanguage?.startsWith('en') ? t('property.detail.bathroom') + 's' : t('property.detail.bathroom')}
            </InfoItem>
            <InfoItem>
              <span role="img" aria-label="size"><AreaIcon /></span> {property.size} {t('property.detail.sqm') || 'sqm'}
            </InfoItem>
          </InfoRow>
          <Price>{parseInt(property.price, 10).toLocaleString()}$</Price>
        </CardContentOverlay>
      </ImageContainer>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  width: 100%;
  max-width: 340px;
  height: auto;
  aspect-ratio: 1;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 400px) {
    max-width: 100%;
    border-radius: 16px;
  }
  
  &:hover {
    box-shadow: 0 4px 24px rgba(0,0,0,0.16);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`;

const StatusTag = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: ${STATUS_BG};
  color: ${STATUS_COLOR};
  font-weight: 600;
  font-size: 16px;
  padding: 4px 16px;
  border-radius: 8px;
  z-index: 2;
`;

const CardContentOverlay = styled.div`
  position: relative;
  z-index: 2;
  padding: 10px 16px;
  background: ${OVERLAY_BG};
  color: ${OVERLAY_TEXT_COLOR};
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: ${OVERLAY_TEXT_COLOR};
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: ${OVERLAY_TEXT_COLOR};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Price = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${PRICE_COLOR};
  text-align: right;
  text-shadow: ${PRICE_SHADOW};
`; 